import { useQuery } from 'react-query';

import { useRequest, queryClient, type QueryFunction } from '@frontend/api';

import type { ActionDownfilterParams, Resolution } from './types';

type RuleGroupPermissionsQueryParams = ActionDownfilterParams &
  ({ quoteItemId: string } | { quoteId: string });

export type RuleGroupPermissionResolution = {
  resolution: Pick<Resolution, 'type' | 'parameters' | 'customLabel'>;
  applicableCount: number;
  applicableComponentIds: number[];
  alreadyResolvedCount: number;
  redundantCount: number;
  forbiddenCount: number;
  userNotPermittedCount: number;
  totalCount: number;
  warningMessage: string | null;
};

type RuleGroupPermissionsResponseType = {
  resolutions: RuleGroupPermissionResolution[];
};

export const getRuleGroupPermissionsKey = (
  params: RuleGroupPermissionsQueryParams,
) => ['rules', 'actions', 'rule_group_res_permissions', JSON.stringify(params)];

export const useGetRuleGroupPermissions: QueryFunction<
  RuleGroupPermissionsResponseType,
  [RuleGroupPermissionsQueryParams],
  ReturnType<typeof getRuleGroupPermissionsKey>
> = (params, { queryOptions, requestOptions } = {}) => {
  const request = useRequest(requestOptions);

  return useQuery({
    ...queryOptions,
    queryKey: getRuleGroupPermissionsKey(params),
    queryFn: ({ signal }) =>
      request<RuleGroupPermissionsResponseType>(
        '/rules/actions/rule_group_res_permissions',
        {
          query: params,
          init: { signal },
        },
      ),
  });
};

export const invalidateRuleGroupPermissionsQuery = (
  params: RuleGroupPermissionsQueryParams,
) => {
  queryClient.invalidateQueries(getRuleGroupPermissionsKey(params));
};
